import React, { useState, useEffect } from 'react';
import { TreeTable, TreeTableTogglerTemplateOptions } from 'primereact/treetable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { TreeNode } from 'primereact/treenode';
import { Menu, MenuItem, TextField, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import AddComponenteModal from './AddComponenteModal';
import RevisaoEspacadaViewer from './RevisaoEspacadaViewer';
import RevisaoEspacadaEdicaoPage from './RevisaoEspacadaEdicaoPage';
import Toast from './Toast';
import './RevisaoEspacadaPage.css';
import { fetchBaralhos, createBaralho, updateBaralho, deleteBaralho, getBaralhoDeleteInfo, fetchItensRevisaoEspacadaByBaralho } from '../services/api';

interface Baralho {
  id: number;
  nome: string;
  baralhoPai: Baralho | null;
  subBaralhos: Baralho[];
  componentes: any[];
  novos?: number;
  aprender?: number;
  revisar?: number;
}

interface RevisaoEspacadaPageProps {
  usuarioId: number;
}

const RevisaoEspacadaPage: React.FC<RevisaoEspacadaPageProps> = ({ usuarioId }) => {
  const [baralhos, setBaralhos] = useState<TreeNode[]>([]);
  const [selectedBaralho, setSelectedBaralho] = useState<Baralho | null>(null);
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [isAddSubBaralho, setIsAddSubBaralho] = useState(false);
  const [isDeleteConfirm, setIsDeleteConfirm] = useState(false);
  const [newName, setNewName] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<{ [key: string]: boolean }>({});
  const [deleteInfo, setDeleteInfo] = useState({ subBaralhosCount: 0, cardsCount: 0 });
  const [isAddComponentModalOpen, setIsAddComponentModalOpen] = useState(false);
  const [selectedBaralhoIds, setSelectedBaralhoIds] = useState<number[]>([]); 
  const [isRevisaoViewerOpen, setIsRevisaoViewerOpen] = useState(false); 
  const [isFullScreen, setIsFullScreen] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const fetchData = async () => {
      const response = await fetchBaralhos();
      const baralhosData = await transformData(response.data);
      setBaralhos(baralhosData);
    };
    fetchData();

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  const transformData = async (data: Baralho[]): Promise<TreeNode[]> => {
    return Promise.all(data.map(async (item): Promise<TreeNode> => {
      const { data: itensRevisao } = await fetchItensRevisaoEspacadaByBaralho(item.id);
      const today = new Date().toLocaleDateString('en-CA');
      const novos = itensRevisao.filter((item: any) => item.status === 'novo' && item.data_proxima_revisao <= today).length;
      const aprender = itensRevisao.filter((item: any) => item.status === 'aprender' && item.data_proxima_revisao <= today).length;
      const revisar = itensRevisao.filter((item: any) => item.status === 'a revisar' && item.data_proxima_revisao <= today).length;

      return {
        key: item.id.toString(),
        data: {
          id: item.id,
          nome: item.nome,
          novos,
          aprender,
          revisar,
        },
        children: item.subBaralhos ? await transformData(item.subBaralhos) : []
      };
    }));
  };

  const getAllBaralhoIds = (node: TreeNode): number[] => {
    let ids = [node.data.id];
    if (node.children && node.children.length > 0) {
      node.children.forEach((childNode) => {
        ids = ids.concat(getAllBaralhoIds(childNode));
      });
    }
    return ids;
  };

  const handleBaralhoClick = (node: TreeNode) => {
    const ids = getAllBaralhoIds(node);
    setSelectedBaralhoIds(ids); 
    setIsRevisaoViewerOpen(true); 
  };

  const handleContextMenu = (event: React.MouseEvent, baralho: Baralho) => {
    event.preventDefault();
    setSelectedBaralho(baralho);
    setContextMenu({ mouseX: event.clientX - 2, mouseY: event.clientY - 4 });
  };

  const handleRenameBaralho = (baralho: Baralho) => {
    setSelectedBaralho(baralho);
    setNewName(baralho.nome);
    setIsRenaming(true);
    setContextMenu(null);
  };

  const handleSaveRename = async () => {
    if (selectedBaralho) {
      await updateBaralho(selectedBaralho.id, newName);
      const response = await fetchBaralhos();
      const baralhosData = await transformData(response.data);
      setBaralhos(baralhosData);
      setIsRenaming(false);
    }
  };

  const handleAddBaralho = async () => {
    const nomeBaralho = prompt('Digite o nome do novo baralho:');
    if (nomeBaralho) {
      await createBaralho(usuarioId, nomeBaralho);
      const response = await fetchBaralhos();
      const baralhosData = await transformData(response.data);
      setBaralhos(baralhosData);
    }
  };

  const handleAddSubBaralho = async () => {
    if (selectedBaralho) {
      const nomeSubBaralho = newName;
      if (nomeSubBaralho) {
        await createBaralho(usuarioId, nomeSubBaralho, selectedBaralho.id);
        const response = await fetchBaralhos();
        const baralhosData = await transformData(response.data);
        setBaralhos(baralhosData);
      }
      setIsAddSubBaralho(false);
      setNewName('');
    }
  };

  const handleDeleteBaralho = async () => {
    if (selectedBaralho) {
      const { data } = await getBaralhoDeleteInfo(selectedBaralho.id);
      setDeleteInfo(data);
      setIsDeleteConfirm(true);
    }
  };

  const confirmDeleteBaralho = async () => {
    if (selectedBaralho) {
      await deleteBaralho(selectedBaralho.id);
      const response = await fetchBaralhos();
      const baralhosData = await transformData(response.data);
      setBaralhos(baralhosData);
      setIsDeleteConfirm(false);
    }
  };

  const calculateTotals = (node: TreeNode) => {
    let novos = node.data.novos || 0;
    let aprender = node.data.aprender || 0;
    let revisar = node.data.revisar || 0;

    if (node.children && node.children.length > 0) {
      node.children.forEach((childNode: TreeNode) => {
        const subTotals = calculateTotals(childNode);
        novos += subTotals.novos;
        aprender += subTotals.aprender;
        revisar += subTotals.revisar;
      });
    }

    return { novos, aprender, revisar };
  };

  const onToggle = (event: { value: any }) => {
    setExpandedKeys(event.value);
  };

  const togglerTemplate = (node: TreeNode, options: TreeTableTogglerTemplateOptions) => {
    if (!node) {
      return null;
    }

    const expanded = expandedKeys[node.key];
    return (
      <button type="button" className="RevisaoEspacadaPage-togglerButton" style={options.buttonStyle} tabIndex={-1} onClick={options.onClick}>
        {expanded ? <ExpandMoreIcon /> : <KeyboardArrowRightIcon />}
      </button>
    );
  };

  const folderTemplate = (node: TreeNode) => {
    const expanded = expandedKeys[node.key];
    return (
      <>
        {expanded ? <FolderOpenIcon /> : <FolderIcon />}
        <span className="RevisaoEspacadaPage-baralhoNome" onClick={() => handleBaralhoClick(node)}>{node.data.nome}</span>
      </>
    );
  };

  const actionTemplate = (node: TreeNode) => {
    return (
      <IconButton className="RevisaoEspacadaPage-actionButton" onClick={(event) => handleContextMenu(event, node.data)}>
        <MoreVertIcon />
      </IconButton>
    );
  };

  const footerGroup = (
    <ColumnGroup>
      <Row>
        <Column footer="Totais:" colSpan={1} className="RevisaoEspacadaPage-footerLabel" />
        <Column footer={baralhos.reduce((sum, node) => sum + calculateTotals(node).novos, 0)} className="RevisaoEspacadaPage-totalNovos" />
        <Column footer={baralhos.reduce((sum, node) => sum + calculateTotals(node).aprender, 0)} className="RevisaoEspacadaPage-totalAprender" />
        <Column footer={baralhos.reduce((sum, node) => sum + calculateTotals(node).revisar, 0)} className="RevisaoEspacadaPage-totalRevisar" />
        <Column footer={null} />
      </Row>
    </ColumnGroup>
  );

  const header = <div className="RevisaoEspacadaPage-header">Revisão Espaçada</div>;

  const handleSaveComponent = async (type: string, data: any, baralhoId: number) => {
    try {
      const baralhosResponse = await fetchBaralhos();
      const baralhosData = await transformData(baralhosResponse.data);
      setBaralhos(baralhosData);
      setSuccessMessage(`${type} adicionado com sucesso`);
    } catch (error) {
      console.error('Erro ao salvar o componente:', error);
    }
  };

  const handleCloseDialog = async () => {
    setIsRevisaoViewerOpen(false);
    const response = await fetchBaralhos();
    const baralhosData = await transformData(response.data);
    setBaralhos(baralhosData);
  };

  return (
    <div className="RevisaoEspacadaPage-container">
      <Tabs value={tabIndex} onChange={handleTabChange} aria-label="Revisão e Edição">
        <Tab label="Revisão" />
        {/* Esconder tabs em dispositivos móveis */}
        {!isMobile && <Tab label="Edição" className='RevisaoEspacadaTabEdicao' />}
        {!isMobile && <Tab label="Estatísticas" className='RevisaoEspacadaTabEstatisticas' />}
      </Tabs>

      {tabIndex === 0 && (
        <div>
          {header}
          {!isMobile && (
            <Button onClick={handleAddBaralho} variant="contained" color="primary" className="RevisaoEspacadaPage-addBaralhoButton">
              Adicionar Baralho
            </Button>
          )}
          {isRenaming && selectedBaralho && (
            <Dialog open={isRenaming} onClose={() => setIsRenaming(false)}>
              <DialogTitle className="RevisaoEspacadaPage-dialogTitle">Renomear Baralho</DialogTitle>
              <DialogContent className="RevisaoEspacadaPage-dialogContent">
                <TextField
                  label="Novo nome"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  fullWidth
                />
              </DialogContent>
              <DialogActions className="RevisaoEspacadaPage-dialogActions">
                <Button onClick={() => setIsRenaming(false)} color="primary">Cancelar</Button>
                <Button onClick={handleSaveRename} color="primary">Salvar</Button>
              </DialogActions>
            </Dialog>
          )}
          {isAddSubBaralho && selectedBaralho && (
            <Dialog open={isAddSubBaralho} onClose={() => setIsAddSubBaralho(false)}>
              <DialogTitle className="RevisaoEspacadaPage-dialogTitle">Adicionar Sub-Baralho</DialogTitle>
              <DialogContent className="RevisaoEspacadaPage-dialogContent">
                <TextField
                  label="Nome do Sub-Baralho"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  fullWidth
                />
              </DialogContent>
              <DialogActions className="RevisaoEspacadaPage-dialogActions">
                <Button onClick={() => setIsAddSubBaralho(false)} color="primary">Cancelar</Button>
                <Button onClick={handleAddSubBaralho} color="primary">Adicionar</Button>
              </DialogActions>
            </Dialog>
          )}
          {isDeleteConfirm && selectedBaralho && (
            <Dialog open={isDeleteConfirm} onClose={() => setIsDeleteConfirm(false)}>
              <DialogTitle className="RevisaoEspacadaPage-dialogTitle">Confirmar Exclusão</DialogTitle>
              <DialogContent className="RevisaoEspacadaPage-dialogContent">
                Tem certeza de que deseja excluir este baralho e todos os seus sub-baralhos e cartões? Isto irá excluir {deleteInfo.subBaralhosCount} sub-baralhos e {deleteInfo.cardsCount} cartões.
              </DialogContent>
              <DialogActions className="RevisaoEspacadaPage-dialogActions">
                <Button onClick={() => setIsDeleteConfirm(false)} color="primary">Cancelar</Button>
                <Button onClick={confirmDeleteBaralho} color="secondary">Excluir</Button>
              </DialogActions>
            </Dialog>
          )}
          {baralhos.length === 0 ? (
            <p className="RevisaoEspacadaPage-noBaralhosMessage">Nenhum baralho disponível. Adicione um novo baralho para começar.</p>
          ) : (
            <TreeTable
              className='RevisaoEspacadaPage-tabelaBaralhos'
              value={baralhos}
              expandedKeys={expandedKeys}
              onToggle={onToggle}
              togglerTemplate={togglerTemplate}
              footerColumnGroup={footerGroup}
              tableStyle={{ minWidth: '100%', tableLayout: 'auto' }} // Garantir que o layout seja 'auto'
              showGridlines={true}
              resizableColumns={true}
              sortMode='multiple'
            >
              <Column
                className='RevisaoEspacadaPage-baralhoNomeColumn'
                field="nome"
                header="Baralho"
                expander
                filter={!isMobile} // Esconde o filtro em dispositivos móveis
                filterPlaceholder="Filter by name"
                sortable
                body={folderTemplate}
                style={{ width: 'auto', whiteSpace: 'nowrap' }} // Ajusta a coluna ao conteúdo
              />
              <Column
                field="novos"
                header={isMobile ? '' : 'Novos'} // Esconder header em dispositivos móveis
                sortable
                body={(node: TreeNode) => (
                  <span className="RevisaoEspacadaPage-quantidadeNovos">
                    {calculateTotals(node).novos}
                  </span>
                )}
                className="RevisaoEspacadaPage-columnCenter"
                style={{ width: 'auto', textAlign: 'center' }} // Ajusta a coluna ao conteúdo
              />
              <Column
                field="aprender"
                header={isMobile ? '' : 'Aprender'} // Esconder header em dispositivos móveis
                sortable
                body={(node: TreeNode) => (
                  <span className="RevisaoEspacadaPage-quantidadeAprender">
                    {calculateTotals(node).aprender}
                  </span>
                )}
                className="RevisaoEspacadaPage-columnCenter"
                style={{ width: 'auto', textAlign: 'center' }} // Ajusta a coluna ao conteúdo
              />
              <Column
                field="revisar"
                header={isMobile ? '' : 'A Revisar'} // Esconder header em dispositivos móveis
                sortable
                body={(node: TreeNode) => (
                  <span className="RevisaoEspacadaPage-quantidadeRevisar">
                    {calculateTotals(node).revisar}
                  </span>
                )}
                className="RevisaoEspacadaPage-columnCenter"
                style={{ width: 'auto', textAlign: 'center' }} // Ajusta a coluna ao conteúdo
              />
              {!isMobile && (
                <Column
                  body={actionTemplate}
                  header="Ações"
                  className="TreeTable-Revisao-Acoes"
                  style={{ width: 'auto', textAlign: 'center' }} // Ajusta a coluna ao conteúdo
                />
              )}
            </TreeTable>

          )}

          <Dialog
            open={isRevisaoViewerOpen}
            onClose={handleCloseDialog}
            fullWidth
            fullScreen={true}
            maxWidth={isFullScreen ? 'xl' : 'md'}
            PaperProps={{
              style: isFullScreen
                ? { width: '100%', height: '100%', margin: 0, overflow: 'hidden' }
                : { width: 'auto', height: 'auto', overflowX: 'hidden' },
            }}
          >
            <DialogTitle className="RevisaoEspacadaPage-dialogTitle">
              Revisão Espaçada
              <IconButton
                aria-label={isFullScreen ? 'Restaurar' : 'Maximizar'}
                onClick={() => setIsFullScreen(!isFullScreen)}
                style={{ position: 'absolute', right: 8, top: 8 }}
              >
                {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
              <IconButton
                aria-label="Fechar"
                onClick={handleCloseDialog}
                style={{ position: 'absolute', right: 48, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent className="RevisaoEspacadaPage-dialogContent"
              style={{
                padding: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              <RevisaoEspacadaViewer usuarioId={usuarioId} baralhoIds={selectedBaralhoIds} />
            </DialogContent>
          </Dialog>

          <Menu
            keepMounted
            open={contextMenu !== null}
            onClose={() => setContextMenu(null)}
            anchorReference="anchorPosition"
            anchorPosition={
              contextMenu !== null
                ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                : undefined
            }
          >
            <MenuItem className="RevisaoEspacadaPage-menuItem" onClick={() => handleRenameBaralho(selectedBaralho!)}>Renomear</MenuItem>
            <MenuItem className="RevisaoEspacadaPage-menuItem" onClick={() => {
              setNewName('');
              setIsAddSubBaralho(true);
              setContextMenu(null);
            }}>Adicionar Sub-Baralho</MenuItem>
            <MenuItem className="RevisaoEspacadaPage-menuItem" onClick={handleDeleteBaralho}>Excluir</MenuItem>
            <MenuItem className="RevisaoEspacadaPage-menuItem" onClick={() => setIsAddComponentModalOpen(true)}>Adicionar Componente</MenuItem>
          </Menu>
          {selectedBaralho && (
            <AddComponenteModal
              open={isAddComponentModalOpen}
              onClose={() => setIsAddComponentModalOpen(false)}
              onSave={handleSaveComponent}
              baralhoId={selectedBaralho.id}
              usuarioId={usuarioId}
            />
          )}
        </div>
      )}
      {successMessage && (
        <Toast message={successMessage} onClose={() => setSuccessMessage(null)} />
      )}

      {tabIndex === 1 && (
        <div>
          {/* Conteúdo da aba de Edição */}
          <RevisaoEspacadaEdicaoPage usuarioId={usuarioId} />
        </div>
      )}
    </div>
  );
};

export default RevisaoEspacadaPage;
